import React, { useState, useMemo, useEffect } from 'react';
import { CryptoSelect } from '../atoms/CryptoSelect';
import type { CryptoCurrency } from '../../utils/coingecko';
import type { Payout } from '../../types/payout';

interface UnresolvedTokensBannerProps {
  payouts: Payout[];
  onSave: (updatedPayouts: Payout[]) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collect all unique custom- IDs with their full crypto metadata */
function collectCustomTokens(payouts: Payout[]): Map<string, CryptoCurrency> {
  const map = new Map<string, CryptoCurrency>(); // customId -> CryptoCurrency
  for (const p of payouts) {
    if (p.crypto?.id.startsWith('custom-'))
      map.set(p.crypto.id, { id: p.crypto.id, symbol: p.crypto.symbol, name: p.crypto.name });
    if (p.sentCrypto?.id.startsWith('custom-'))
      map.set(p.sentCrypto.id, { id: p.sentCrypto.id, symbol: p.sentCrypto.symbol, name: p.sentCrypto.name });
    if (p.receivedCrypto?.id.startsWith('custom-'))
      map.set(p.receivedCrypto.id, { id: p.receivedCrypto.id, symbol: p.receivedCrypto.symbol, name: p.receivedCrypto.name });
  }
  return map;
}

function replaceCrypto(
  crypto: Payout['crypto'],
  oldId: string,
  replacement: CryptoCurrency
): Payout['crypto'] {
  if (!crypto || crypto.id !== oldId) return crypto;
  return { id: replacement.id, symbol: replacement.symbol, name: replacement.name };
}

// ---------------------------------------------------------------------------
// Row state: two modes — CoinGecko lookup OR merge into another custom token
// ---------------------------------------------------------------------------

type ResolveMode = 'coingecko' | 'merge';

interface RowState {
  mode: ResolveMode;
  coingeckoCrypto: CryptoCurrency | null;
  mergeTargetId: string | null; // another custom-ID in the data
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const UnresolvedTokensBanner: React.FC<UnresolvedTokensBannerProps> = ({
  payouts,
  onSave,
}) => {
  const customTokens = useMemo(() => collectCustomTokens(payouts), [payouts]);

  // Row state keyed by customId
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Initialise row state for new custom IDs (never overwrite existing user selections)
  useEffect(() => {
    if (customTokens.size === 0) return;
    setRows((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [customId] of customTokens) {
        if (!(customId in next)) {
          next[customId] = { mode: 'coingecko', coingeckoCrypto: null, mergeTargetId: null };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [customTokens]);

  if (customTokens.size === 0) return null;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const setMode = (customId: string, mode: ResolveMode) =>
    setRows((prev) => ({
      ...prev,
      [customId]: { ...prev[customId], mode, coingeckoCrypto: null, mergeTargetId: null },
    }));

  const setCoingecko = (customId: string, crypto: CryptoCurrency | null) =>
    setRows((prev) => ({
      ...prev,
      [customId]: { ...prev[customId], coingeckoCrypto: crypto },
    }));

  const setMergeTarget = (customId: string, targetId: string | null) =>
    setRows((prev) => ({
      ...prev,
      [customId]: { ...prev[customId], mergeTargetId: targetId },
    }));

  /** Returns the effective replacement CryptoCurrency for a given customId, or null if not ready */
  const getEffectiveReplacement = (customId: string): CryptoCurrency | null => {
    const row = rows[customId];
    if (!row) return null;
    if (row.mode === 'coingecko') return row.coingeckoCrypto;
    if (row.mode === 'merge' && row.mergeTargetId) {
      const target = customTokens.get(row.mergeTargetId);
      return target ?? null;
    }
    return null;
  };

  const readyIds = Array.from(customTokens.keys()).filter(
    (id) => getEffectiveReplacement(id) !== null
  );

  const handleApply = async () => {
    if (readyIds.length === 0) return;
    setIsSaving(true);
    try {
      let updated = [...payouts];
      for (const customId of readyIds) {
        const replacement = getEffectiveReplacement(customId)!;
        updated = updated.map((p) => ({
          ...p,
          crypto: replaceCrypto(p.crypto, customId, replacement),
          sentCrypto: replaceCrypto(p.sentCrypto, customId, replacement),
          receivedCrypto: replaceCrypto(p.receivedCrypto, customId, replacement),
        }));
      }
      await onSave(updated);
      setIsOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-6xl mx-auto mb-6">
      {/* Compact banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <svg
            className="w-5 h-5 text-amber-500 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
          <p className="text-sm font-medium text-amber-800">
            {customTokens.size} token{customTokens.size > 1 ? 's' : ''} non reconnu{customTokens.size > 1 ? 's' : ''} —{' '}
            <span className="font-normal text-amber-700">
              associez-les à un coin réel pour corriger vos données.
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
        >
          Corriger
        </button>
      </div>

      {/* Offcanvas overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Offcanvas panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[680px] bg-white dark:bg-gray-800 shadow-2xl z-50 transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">
              {customTokens.size} token{customTokens.size > 1 ? 's' : ''} non reconnu{customTokens.size > 1 ? 's' : ''}
            </h2>
            <p className="text-amber-100 text-sm mt-1">
              Associez chaque token à un coin CoinGecko ou fusionnez-les.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-amber-400 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {Array.from(customTokens.entries()).map(([customId, token]) => {
            const row = rows[customId];
            if (!row) return null;

            const mergeOptions = Array.from(customTokens.values()).filter(
              (t) => t.id !== customId
            );

            return (
              <div
                key={customId}
                className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-4 space-y-3"
              >
                {/* Token label */}
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {token.name}
                    {token.symbol !== token.name && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">({token.symbol})</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{customId}</span>
                </div>

                {/* Mode toggle */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex rounded-lg border border-amber-200 overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => setMode(customId, 'coingecko')}
                      className={`px-3 py-1.5 transition-colors ${
                        row.mode === 'coingecko'
                          ? 'bg-amber-500 text-white font-medium'
                          : 'bg-white dark:bg-gray-800 text-amber-700 hover:bg-amber-50'
                      }`}
                    >
                      Coin réel
                    </button>
                    {mergeOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setMode(customId, 'merge')}
                        className={`px-3 py-1.5 border-l border-amber-200 transition-colors ${
                          row.mode === 'merge'
                            ? 'bg-amber-500 text-white font-medium'
                            : 'bg-white dark:bg-gray-800 text-amber-700 hover:bg-amber-50'
                        }`}
                      >
                        Fusionner avec…
                      </button>
                    )}
                  </div>
                </div>

                {/* Resolver */}
                <div>
                  {row.mode === 'coingecko' ? (
                    <CryptoSelect
                      value={row.coingeckoCrypto}
                      onChange={(crypto) => setCoingecko(customId, crypto)}
                      placeholder="Rechercher le coin réel..."
                    />
                  ) : (
                    <select
                      value={row.mergeTargetId ?? ''}
                      onChange={(e) => setMergeTarget(customId, e.target.value || null)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <option value="">— choisir le token à conserver —</option>
                      {mergeOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.symbol}) — {t.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Summary line when ready */}
                {getEffectiveReplacement(customId) && (
                  <p className="text-xs text-green-700 bg-green-50 rounded px-3 py-2">
                    {row.mode === 'coingecko' ? (
                      <>
                        Sera remplacé par{' '}
                        <strong>{row.coingeckoCrypto!.name}</strong>{' '}
                        <code className="bg-green-100 px-1 rounded">{row.coingeckoCrypto!.id}</code>
                      </>
                    ) : (
                      <>
                        Sera fusionné dans{' '}
                        <strong>{customTokens.get(row.mergeTargetId!)?.name}</strong>{' '}
                        <code className="bg-green-100 px-1 rounded">{row.mergeTargetId}</code>
                        {' '}— toutes les transactions utiliseront le même ID.
                      </>
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:dark:bg-gray-900 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleApply}
            disabled={readyIds.length === 0 || isSaving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving
              ? 'Correction en cours...'
              : `Corriger ${readyIds.length > 0 ? readyIds.length : ''} token${readyIds.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
